(function () {
  const STORAGE_KEY = "coloures_notify_me_pending";
  const MAX_PENDING_AGE_MS = 24 * 60 * 60 * 1000;
  const MIN_RETRY_INTERVAL_MS = 5 * 60 * 1000;
  const MAX_DELIVERY_ATTEMPTS = 6;
  const OPTIMISTIC_TRANSITION_MS = 300;

  let retryEndpoint = "";
  let lifecycleListenersBound = false;

  function parseJson(value, fallback) {
    if (!value) return fallback;
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  function formEncode(data) {
    const body = new URLSearchParams();
    Object.keys(data).forEach((key) => body.append(key, data[key] ?? ""));
    return body;
  }

  function getSelectedOptions(variantSelects) {
    if (!variantSelects) return [];
    return Array.from(variantSelects.querySelectorAll("fieldset, .product-form__input--dropdown"))
      .map((group) => {
        const selected = group.querySelector("input:checked") || group.querySelector("select option[selected]");
        if (!selected) return null;
        return { name: selected.dataset.optionName || "", value: selected.value || "" };
      })
      .filter(Boolean);
  }

  function getSize(variantSelects, variant) {
    const selectedOptions = getSelectedOptions(variantSelects);
    const sizeOption = selectedOptions.find((option) => /size/i.test(option.name));
    if (sizeOption) return sizeOption.value;
    if (selectedOptions.length === 1) return selectedOptions[0].value;
    return variant?.title || "";
  }

  function getCurrentColour(component) {
    const productInfo = component.closest("product-info");
    const activeLinkedColour = productInfo?.querySelector(".linked-color-swatches__active")?.textContent?.trim();
    return activeLinkedColour || component.dataset.currentColour || component.dataset.productTitle || "";
  }

  function generateRequestId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();

    if (window.crypto?.getRandomValues) {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }

    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  }

  function writePendingQueue(queue) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  }

  function readPendingQueue() {
    const now = Date.now();
    const stored = parseJson(window.localStorage.getItem(STORAGE_KEY), []);
    const queue = Array.isArray(stored) ? stored : [];
    const active = queue.filter((item) => {
      const expiresAt = Date.parse(item?.expires_at || "");
      return item?.request_id && Number.isFinite(expiresAt) && expiresAt > now;
    });

    if (active.length !== queue.length) writePendingQueue(active);
    return active;
  }

  function queueRequest(payload) {
    const now = Date.now();
    const queue = readPendingQueue();
    let requestId = payload.request_id;
    while (queue.some((item) => item.request_id === requestId)) requestId = generateRequestId();

    const pending = {
      ...payload,
      request_id: requestId,
      created_at: new Date(now).toISOString(),
      expires_at: new Date(now + MAX_PENDING_AGE_MS).toISOString(),
      attempts: 0,
      last_attempt_at: 0
    };
    queue.push(pending);
    writePendingQueue(queue);
    return pending;
  }

  function deliveryPayload(item) {
    return {
      request_id: item.request_id,
      email: item.email,
      product_title: item.product_title,
      product_handle: item.product_handle,
      variant_id: item.variant_id,
      sku: item.sku,
      colour: item.colour,
      size: item.size,
      variant_title: item.variant_title,
      product_url: item.product_url,
      website: ""
    };
  }

  function processPendingQueue(endpoint, requestId) {
    if (!endpoint || window.navigator.onLine === false) return;

    try {
      const now = Date.now();
      const queue = readPendingQueue();
      const due = queue.filter((item) => {
        if (requestId && item.request_id !== requestId) return false;
        if (Number(item.attempts || 0) >= MAX_DELIVERY_ATTEMPTS) return false;
        const lastAttemptAt = Number(item.last_attempt_at || 0);
        return lastAttemptAt === 0 || now - lastAttemptAt >= MIN_RETRY_INTERVAL_MS;
      });

      if (!due.length) return;

      const dueIds = new Set(due.map((item) => item.request_id));
      queue.forEach((item) => {
        if (!dueIds.has(item.request_id)) return;
        item.attempts = Number(item.attempts || 0) + 1;
        item.last_attempt_at = now;
      });
      writePendingQueue(queue);

      due.forEach((item) => {
        try {
          fetch(endpoint, {
            method: "POST",
            mode: "no-cors",
            keepalive: true,
            body: formEncode(deliveryPayload(item))
          }).catch(() => {});
        } catch (error) {
          // The durable queue remains available for a later lifecycle retry.
        }
      });
    } catch (error) {
      // Queue access failures during background retries must not alter customer-facing state.
    }
  }

  function initializeRetryWorker(endpoint) {
    retryEndpoint = endpoint || retryEndpoint;
    processPendingQueue(retryEndpoint);

    if (lifecycleListenersBound) return;
    lifecycleListenersBound = true;

    window.addEventListener("online", () => processPendingQueue(retryEndpoint));
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") processPendingQueue(retryEndpoint);
    });
  }

  function hasRequiredPayload(payload) {
    return [
      payload.email,
      payload.product_title,
      payload.product_handle,
      payload.variant_id,
      payload.colour,
      payload.size,
      payload.variant_title,
      payload.product_url
    ].every((value) => String(value || "").trim() !== "");
  }

  class NotifyMe extends HTMLElement {
    connectedCallback() {
      this.closedView = this.querySelector('[data-notify-view="closed"]');
      this.panel = this.querySelector('[data-notify-view="panel"]');
      this.formView = this.querySelector('[data-notify-view="form"]');
      this.sendingView = this.querySelector('[data-notify-view="sending"]');
      this.postView = this.querySelector('[data-notify-view="post"]');
      this.toggleButton = this.querySelector(".notify-me__toggle");
      this.closeButton = this.querySelector(".notify-me__close");
      this.repeatButton = this.querySelector(".notify-me__repeat");
      this.form = this.querySelector(".notify-me__form");
      this.emailInput = this.querySelector('input[name="email"]');
      this.submitButton = this.querySelector(".notify-me__submit");
      this.errorMessage = this.querySelector("[data-notify-error]");
      this.productInfo = this.closest("product-info");
      this.currentVariant = parseJson(this.dataset.initialVariant, null);
      this.variantSkus = this.getVariantSkuMap();
      this.variantPending = false;
      this.isSubmitting = false;
      this.keepPanelOpen = false;

      this.toggleButton?.addEventListener("click", this.openPanel.bind(this));
      this.closeButton?.addEventListener("click", this.closePanel.bind(this));
      this.repeatButton?.addEventListener("click", this.openPanel.bind(this));
      this.form?.addEventListener("submit", this.handleSubmit.bind(this));

      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        this.optionChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
          const eventTarget = event?.data?.event?.target;
          if (!eventTarget || !this.productInfo?.contains(eventTarget)) return;
          this.beginVariantChange();
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.dataset.sectionId) return;
          this.resolveVariantChange(event.data.variant || null);
        });
      }

      this.updateForVariant(false);
      initializeRetryWorker(this.dataset.endpoint);
    }

    disconnectedCallback() {
      this.optionChangeUnsubscriber?.();
      this.variantChangeUnsubscriber?.();
    }

    getVariantSkuMap() {
      const entries = parseJson(this.querySelector("[data-notify-me-variant-skus]")?.textContent, []);
      return new Map(entries.map((entry) => [String(entry.id), entry.sku || ""]));
    }

    getSku(variant) {
      const resolvedSku = typeof variant?.sku === "string" ? variant.sku.trim() : "";
      if (resolvedSku) return resolvedSku;
      return this.variantSkus.get(String(variant?.id || "")) || "";
    }

    setView(view) {
      this.closedView.hidden = view !== "closed";
      this.panel.hidden = view === "closed";
      this.formView.hidden = view !== "form";
      this.sendingView.hidden = view !== "sending";
      this.postView.hidden = view !== "post";
    }

    openPanel() {
      if (this.variantPending || this.isSubmitting) return;
      this.resetForm();
      this.setView("form");
      window.requestAnimationFrame(() => this.emailInput?.focus());
    }

    closePanel() {
      if (this.isSubmitting) return;
      this.resetForm();
      this.setView("closed");
      window.requestAnimationFrame(() => this.toggleButton?.focus());
    }

    beginVariantChange() {
      this.keepPanelOpen = !this.panel.hidden;
      this.variantPending = true;
      this.currentVariant = null;
      this.classList.add("notify-me--hidden");
      this.resetForm();
      this.setView(this.keepPanelOpen ? "form" : "closed");
      this.syncDisabledState();
    }

    resolveVariantChange(variant) {
      this.currentVariant = variant;
      this.variantPending = false;
      this.resetForm();
      this.updateForVariant(true);
      this.syncDisabledState();
    }

    updateForVariant(fromSelectionChange) {
      const variant = this.currentVariant;
      if (!variant || variant.available) {
        this.classList.add("notify-me--hidden");
        this.setView("closed");
        this.keepPanelOpen = false;
        return;
      }

      this.classList.remove("notify-me--hidden");
      this.setView(fromSelectionChange && this.keepPanelOpen ? "form" : "closed");
      this.keepPanelOpen = false;
    }

    resetForm() {
      this.form?.reset();
      if (this.errorMessage) this.errorMessage.hidden = true;
    }

    getPayload() {
      const variant = this.currentVariant;
      const variantSelects = this.productInfo?.querySelector("variant-selects");
      return {
        email: this.emailInput.value.trim().toLowerCase(),
        product_title: this.dataset.productTitle || "",
        product_handle: this.dataset.productHandle || "",
        variant_id: variant?.id ? String(variant.id) : "",
        sku: this.getSku(variant),
        colour: getCurrentColour(this),
        size: getSize(variantSelects, variant),
        variant_title: variant?.title || "",
        product_url: this.dataset.productUrl || window.location.pathname,
        website: this.querySelector('input[name="website"]')?.value || ""
      };
    }

    setSubmitting(isSubmitting) {
      this.isSubmitting = isSubmitting;
      this.syncDisabledState();
    }

    syncDisabledState() {
      const disabled = this.isSubmitting || this.variantPending;
      [this.submitButton, this.emailInput, this.toggleButton, this.closeButton, this.repeatButton]
        .filter(Boolean)
        .forEach((control) => { control.disabled = disabled; });
    }

    showError() {
      this.setView("form");
      if (this.errorMessage) this.errorMessage.hidden = false;
    }

    handleSubmit(event) {
      event.preventDefault();
      if (this.isSubmitting || this.variantPending || !this.currentVariant || this.currentVariant.available) return;

      const payload = this.getPayload();
      if (payload.website) return;

      if (!payload.email || !this.emailInput.checkValidity()) {
        this.emailInput.reportValidity();
        return;
      }

      if (!hasRequiredPayload(payload)) {
        this.showError();
        return;
      }

      const submittedVariantId = payload.variant_id;
      let pending;
      try {
        pending = queueRequest({
          request_id: generateRequestId(),
          email: payload.email,
          product_title: payload.product_title,
          product_handle: payload.product_handle,
          variant_id: payload.variant_id,
          sku: payload.sku,
          colour: payload.colour,
          size: payload.size,
          variant_title: payload.variant_title,
          product_url: payload.product_url
        });
      } catch (error) {
        this.showError();
        return;
      }

      this.setSubmitting(true);
      this.setView("sending");
      processPendingQueue(this.dataset.endpoint, pending.request_id);

      window.setTimeout(() => {
        if (!this.variantPending && String(this.currentVariant?.id) === submittedVariantId) {
          this.form.reset();
          this.setView("post");
        }
        this.setSubmitting(false);
      }, OPTIMISTIC_TRANSITION_MS);
    }
  }

  if (!customElements.get("notify-me")) {
    customElements.define("notify-me", NotifyMe);
  }
})();
